import { useEffect, useMemo, useState } from "react";
import Modal from "@modalSrc/Modal";
import {
  EMAIL_CONTEXT_SOURCES,
  buildSourceValue,
  formatContextPlaceholder,
} from "@/utils/emailTemplate";

type EmailContextVariableSelectModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (source: string) => void;
  zIndex?: number;
};

export default function EmailContextVariableSelectModal(props: EmailContextVariableSelectModalProps) {
  const [dynamicSuffixes, setDynamicSuffixes] = useState<Record<string, string>>({
    testField: "",
    instanceData: "",
  });

  const contextGroups = useMemo(
    () => Array.from(new Set(EMAIL_CONTEXT_SOURCES.map((s) => s.group))),
    []
  );

  useEffect(() => {
    if (!props.isOpen) return;
    setDynamicSuffixes({ testField: "", instanceData: "" });
  }, [props.isOpen]);

  function handleSelect(source: string) {
    if (!source) return;
    props.onSelect(source);
    props.onClose();
  }

  if (!props.isOpen) return null;

  return (
    <Modal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title="Select Context Variable"
      className="emailContextVariableSelectModal"
      zIndex={props.zIndex ?? 9200}
    >
      <p className="emailTemplateHint">
        Choose a variable to insert at the cursor. Placeholders use the context path, e.g.{" "}
        <code>{"{test.email}"}</code>.
      </p>
      <div className="emailContextPalette">
        {contextGroups.map((group) => (
          <section key={group} className="emailContextGroup">
            <h4 className="emailContextGroupTitle">{group}</h4>
            <div className="emailContextChips">
              {EMAIL_CONTEXT_SOURCES.filter((option) => option.group === group).map((option) => {
                if (option.dynamicSuffix) {
                  const suffix = dynamicSuffixes[option.source] || "";
                  const source = buildSourceValue(option.source, suffix);
                  const canInsert = !!source;
                  return (
                    <div key={option.source} className="emailContextDynamicChip">
                      <span className="emailContextDynamicLabel">{option.label}</span>
                      <input
                        type="text"
                        className="emailContextDynamicInput"
                        title={option.suffixPlaceholder}
                        placeholder={option.suffixPlaceholder}
                        value={suffix}
                        onChange={(e) =>
                          setDynamicSuffixes((prev) => ({
                            ...prev,
                            [option.source]: e.target.value,
                          }))
                        }
                      />
                      <button
                        type="button"
                        className="emailContextChip"
                        title={
                          canInsert
                            ? `Insert ${formatContextPlaceholder(source)}`
                            : `Enter a ${option.label.toLowerCase()} name first`
                        }
                        disabled={!canInsert}
                        onClick={() => handleSelect(source)}
                      >
                        <span className="emailContextChipLabel">{option.label}</span>
                        {canInsert ? (
                          <code className="emailContextChipCode">{formatContextPlaceholder(source)}</code>
                        ) : (
                          <span className="emailContextChipHint">Enter a name above, then insert</span>
                        )}
                      </button>
                    </div>
                  );
                }

                return (
                  <button
                    key={option.source}
                    type="button"
                    className="emailContextChip"
                    title={`Insert ${formatContextPlaceholder(option.source)}`}
                    onClick={() => handleSelect(option.source)}
                  >
                    <span className="emailContextChipLabel">{option.label}</span>
                    <code className="emailContextChipCode">{formatContextPlaceholder(option.source)}</code>
                  </button>
                );
              })}
            </div>
          </section>
        ))}
      </div>
      <div className="emailContextVariableSelectActions">
        <button type="button" onClick={props.onClose}>
          Cancel
        </button>
      </div>
    </Modal>
  );
}
